import { CheckCircle, DirectionsRun, Login as LoginIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEntraAuth } from '../../contexts/EntraAuthContext';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';

const Login: React.FC = () => {
  const { initiateOAuth } = useOAuthPopup();
  const { state } = useAuth();
  const { isAuthenticated: isEntraAuthenticated, user } = useEntraAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to Entra sign-in if not authenticated with Microsoft
  if (!isEntraAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Redirect to dashboard if Intervals.icu is connected
  if (state.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async () => {
    try {
      setError(null);
      setIsLoading(true);
      await initiateOAuth();
    } catch (err) {
      console.error('Intervals.icu login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        py={4}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box textAlign="center" mb={4}>
            <DirectionsRun sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" component="h1" gutterBottom>
              PacePilot
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your Personal Running Training Assistant
            </Typography>
          </Box>

          {/* Signed in status */}
          <Box textAlign="center" mb={3}>
            <Chip
              icon={<CheckCircle />}
              label={`Signed in as ${user?.name || user?.username || 'User'}`}
              color="success"
              variant="outlined"
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box mb={3}>
            <Typography variant="body1" align="center" color="text.secondary" gutterBottom>
              Connect your Intervals.icu account to access your training data
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box mb={3}>
            <Button
              variant="contained"
              size="large"
              startIcon={isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
              onClick={handleLogin}
              disabled={isLoading}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {isLoading ? 'Connecting...' : 'Connect with Intervals.icu'}
            </Button>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" align="center" color="text.secondary">
              By connecting, you'll get access to:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Your current threshold pace and training zones
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Weekly mileage and fitness metrics (ATL, CTL, HRV)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Personalized training plan generation
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Calendar view of your scheduled workouts
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
